import { forwardRef, useId, useState } from 'react'

import { cn } from '@/lib/utils'
import { FxIcon } from './FxIcon'

/**
 * The design system's `TextField`: a label above a 40px field that carries no
 * border — it is lifted off the ground by `shadow-raised` like everything else
 * in the design. On a white page (the auth screens) the fill flips to `ground`
 * so the field is still visible; that is what `onWhite` is for.
 *
 * Focus draws a 2px inner ring rather than the global focus outline, because
 * an outline outside a shadow-lifted field reads as a second border. Figma
 * shows no focus state at all; this one comes from the reference components.
 *
 * A password field grows an eye button that toggles visibility, which is why
 * `type` is read here instead of being passed straight through.
 */
const fieldStyles =
  'h-control w-full rounded-md px-4 text-body text-text shadow-raised outline-none ' +
  'placeholder:text-text-muted focus-visible:outline-none ' +
  'focus:shadow-[shadow:var(--fx-shadow-raised),inset_0_0_0_2px_hsl(var(--fx-primary-strong))] ' +
  'disabled:cursor-not-allowed disabled:text-text-on-disabled'

export interface FxTextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  /** Adds the `danger` star after the label. Also sets `required` on the input. */
  required?: boolean
  /** Fill with `ground` instead of `surface`, for the white auth pages. */
  onWhite?: boolean
  /**
   * Beyond the design system's contract: something drawn inside the right end
   * of the field — the "bestätigt" badge on the e-mail row in Einstellungen.
   * Ignored on a password field, whose right end belongs to the eye button.
   */
  trailing?: React.ReactNode
  /** Applied to the label + field column rather than to the input. */
  containerClassName?: string
  /**
   * Beyond the design system's contract, and the one thing the form screens
   * cannot do without: a line under the field. `error` replaces `hint` and
   * marks the field invalid, because two lines saying different things about
   * one field is worse than one.
   */
  hint?: string
  error?: string
}

export const FxTextField = forwardRef<HTMLInputElement, FxTextFieldProps>(
  (
    { className, containerClassName, label, required, onWhite, trailing, hint, error, type, id, ...props },
    ref,
  ) => {
    const [shown, setShown] = useState(false)
    const generatedId = useId()
    const inputId = id ?? generatedId
    const isPassword = type === 'password'
    const note = error ?? hint
    const noteId = note ? `${inputId}-note` : undefined

    const input = (
      <input
        id={inputId}
        ref={ref}
        type={isPassword ? (shown ? 'text' : 'password') : (type ?? 'text')}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={noteId}
        className={cn(
          fieldStyles,
          onWhite ? 'bg-ground' : 'bg-surface',
          error && 'shadow-[shadow:var(--fx-shadow-raised),inset_0_0_0_2px_hsl(var(--fx-danger))]',
          (isPassword || trailing) && 'pr-10',
          className,
        )}
        {...props}
      />
    )

    const field =
      isPassword || trailing ? (
        <div className="relative">
          {input}
          {isPassword ? (
            <button
              type="button"
              onClick={() => setShown((v) => !v)}
              aria-label={shown ? 'Passwort verbergen' : 'Passwort anzeigen'}
              className="absolute right-3 top-1/2 flex -translate-y-1/2 text-text-muted"
            >
              <FxIcon name={shown ? 'visibility_off' : 'visibility'} size={20} filled />
            </button>
          ) : (
            <span className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center">
              {trailing}
            </span>
          )}
        </div>
      ) : (
        input
      )

    const noteLine = note ? (
      <p id={noteId} className={cn('text-caption', error ? 'text-danger-text' : 'text-text-muted')}>
        {note}
      </p>
    ) : null

    if (!label) {
      return noteLine ? (
        <div className={cn('flex flex-col gap-1', containerClassName)}>
          {field}
          {noteLine}
        </div>
      ) : (
        field
      )
    }

    return (
      <div className={cn('flex flex-col gap-2', containerClassName)}>
        <label htmlFor={inputId} className="text-body text-text">
          {label}
          {required && <span className="text-danger-text"> *</span>}
        </label>
        {field}
        {noteLine}
      </div>
    )
  },
)
FxTextField.displayName = 'FxTextField'
